/**
 * PDF Viewer Modal with Text Search
 * ==================================
 * Opens source documents to specific pages for transaction verification.
 * 
 * Features:
 * - Opens PDF to specific page
 * - Page navigation (prev/next)
 * - Zoom controls
 * - Download option
 * - Shows transaction context
 * - TEXT SEARCH with highlighting
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, Download, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, ExternalLink, Loader2, Search, ArrowUp, ArrowDown, FileText, AlertTriangle } from 'lucide-react';
import api from '../../utils/api';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { FinancialValue } from '../shared/FinancialValue';

interface TransactionContext {
  date: string;
  vendor: string;
  amount: number;
  description?: string;
}

interface BBox {
  Left: number;
  Top: number;
  Width: number;
  Height: number;
}

interface SearchResult {
  pageNumber: number;
  lineIndex: number;
  text: string;
  matchStart: number;
  matchEnd: number;
  bbox?: BBox;
}

interface PageText {
  page_number: number;
  line_count: number;
  lines: Array<{ text: string; confidence: number; bbox?: BBox }>;
  full_text: string;
}

interface PDFViewerModalProps {
  isOpen: boolean;
  reportId: string;
  sourceFile: string;
  sourcePage: number;
  transactionContext?: TransactionContext;
  pdfUrlOverride?: string; // Direct presigned URL — skips source-pdf endpoint lookup
  onClose: () => void;
}

export default function PDFViewerModal({
  isOpen,
  reportId,
  sourceFile,
  sourcePage,
  transactionContext,
  pdfUrlOverride,
  onClose
}: PDFViewerModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  useFocusTrap(modalRef, isOpen);

  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(sourcePage || 1);
  const [totalPages, setTotalPages] = useState(1);
  const [zoom, setZoom] = useState(100);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [currentResultIndex, setCurrentResultIndex] = useState(0);
  const [isSearching, setIsSearching] = useState(false);
  const [pdfText, setPdfText] = useState<PageText[]>([]);
  const [showSearchPanel, setShowSearchPanel] = useState(false);

  // Fetch PDF URL when modal opens
  useEffect(() => {
    if (isOpen && sourceFile && reportId) {
      fetchPdfUrl();
    }
  }, [isOpen, sourceFile, reportId]);

  // Update page when sourcePage changes - but cap at totalPages
  useEffect(() => {
    if (sourcePage && totalPages > 0) {
      setCurrentPage(Math.min(sourcePage, totalPages));
    } else if (sourcePage) {
      setCurrentPage(sourcePage);
    }
  }, [sourcePage, totalPages]);

  // Auto-search for transaction amount when modal opens with context
  useEffect(() => {
    if (isOpen && transactionContext && pdfText.length > 0 && !searchQuery) {
      const amount = Math.abs(transactionContext.amount);
      // Format with commas (e.g., "2,495.69") since bank statements use comma-formatted amounts
      const amountWithCommas = amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      setSearchQuery(amountWithCommas);
      setShowSearchPanel(true);
      // Directly trigger search with comma-formatted amount (avoids stale state issue)
      handleSearch(amountWithCommas);
    }
  }, [isOpen, transactionContext, pdfText]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't handle if typing in search input
      if ((e.target as HTMLElement).tagName === 'INPUT') {
        if (e.key === 'Enter' && searchQuery) {
          e.preventDefault();
          handleSearch();
        }
        return;
      }
      
      if (e.key === 'ArrowLeft') {
        handlePreviousPage();
      } else if (e.key === 'ArrowRight') {
        handleNextPage();
      } else if (e.key === 'Escape') {
        onClose();
      } else if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
        e.preventDefault();
        setShowSearchPanel(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentPage, totalPages, searchQuery]);

  const fetchPdfUrl = async () => {
    setLoading(true);
    setError(null);

    // If caller provided a direct presigned URL, use it without API call
    if (pdfUrlOverride) {
      setPdfUrl(pdfUrlOverride);
      setLoading(false);
      return;
    }

    try {
      const params = new URLSearchParams({ filename: sourceFile });
      const response = await api.get(`/api/v1/reconciliation/source-pdf/${reportId}?${params.toString()}`);
      
      // Try presigned URL first (simpler, works in iframe without auth)
      if (response.url) {
        setPdfUrl(response.url);
        setTotalPages(response.total_pages || 1);

        // If we have transaction context, pre-fetch PDF text for auto-search
        if (transactionContext) {
          fetchPdfText();
        }
      } else {
        throw new Error('No URL returned');
      }
    } catch (err: any) {
      console.error('Error fetching PDF URL:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to load document');
    } finally {
      setLoading(false);
    }
  };

  const fetchPdfText = async () => {
    try {
      setIsSearching(true);
      const params = new URLSearchParams({ filename: sourceFile });
      const response = await api.get(`/api/v1/reconciliation/source-pdf-text/${reportId}?${params.toString()}`);
      
      if (response.pages) {
        setPdfText(response.pages);
        return response.pages;
      }
      return [];
    } catch (err) {
      console.error('Error fetching PDF text:', err);
      return [];
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearch = async (queryOverride?: string) => {
    const searchTerm = (queryOverride || searchQuery).trim();
    if (!searchTerm) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);

    // Fetch text if not already loaded
    let textData = pdfText;
    if (textData.length === 0) {
      textData = await fetchPdfText();
    }

    // Search through all pages
    const results: SearchResult[] = [];
    const query = searchTerm.toLowerCase();

    for (const page of textData) {
      page.lines.forEach((line, lineIndex) => {
        const lineText = line.text.toLowerCase();
        let startIndex = 0;
        let matchIndex;

        while ((matchIndex = lineText.indexOf(query, startIndex)) !== -1) {
          results.push({
            pageNumber: page.page_number,
            lineIndex,
            text: line.text,
            matchStart: matchIndex,
            matchEnd: matchIndex + query.length,
            bbox: line.bbox
          });
          startIndex = matchIndex + 1;
        }
      });
    }

    setSearchResults(results);
    setCurrentResultIndex(0);
    setIsSearching(false);

    // Jump to first result
    if (results.length > 0) {
      setCurrentPage(results[0].pageNumber);
    }
  };

  const goToNextResult = () => {
    if (searchResults.length === 0) return;
    const nextIndex = (currentResultIndex + 1) % searchResults.length;
    setCurrentResultIndex(nextIndex);
    setCurrentPage(searchResults[nextIndex].pageNumber);
  };

  const goToPreviousResult = () => {
    if (searchResults.length === 0) return;
    const prevIndex = (currentResultIndex - 1 + searchResults.length) % searchResults.length;
    setCurrentResultIndex(prevIndex);
    setCurrentPage(searchResults[prevIndex].pageNumber);
  };

  const handlePreviousPage = useCallback(() => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  }, [currentPage]);

  const handleNextPage = useCallback(() => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => Math.min(prev + 1, totalPages));
    }
  }, [currentPage, totalPages]);

  const handleZoomIn = () => {
    if (zoom < 200) {
      setZoom(prev => prev + 25);
    }
  };

  const handleZoomOut = () => {
    if (zoom > 50) {
      setZoom(prev => prev - 25);
    }
  };

  const handleDownload = () => {
    if (pdfUrl) {
      window.open(pdfUrl, '_blank');
    }
  };

  const handleOpenInNewTab = () => {
    if (pdfUrl) {
      window.open(`${pdfUrl}#page=${currentPage}`, '_blank');
    }
  };

  const handlePageInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const page = parseInt(e.target.value);
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const jumpToTransactionPage = () => {
    setCurrentPage(Math.min(sourcePage, totalPages));
  };

  // Get current page search results for highlighting
  const currentPageResults = searchResults.filter(r => r.pageNumber === currentPage);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/80 flex items-center justify-center z-50"
      data-testid="pdf-viewer-modal"
    >
      {/* Highlight pulse animation for active search result overlay */}
      <style>{`
        @keyframes pdf-highlight-pulse {
          0%, 100% { opacity: 0.7; }
          50% { opacity: 1; }
        }
        .pdf-highlight-pulse {
          animation: pdf-highlight-pulse 1.5s ease-in-out 3;
        }
      `}</style>
      <div ref={modalRef} role="dialog" aria-modal="true" aria-label={`PDF Viewer - ${sourceFile}`} className="rounded-lg shadow-2xl w-full max-w-6xl h-[90vh] flex flex-col" style={{ background: 'var(--color-bg-surface)' }}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 rounded-t-lg" style={{ borderBottom: '1px solid var(--color-border)', background: 'var(--color-bg-elevated)' }}>
          <div className="flex-1 min-w-0">
            <h2 className="cs-h3 truncate" title={sourceFile}>
              {sourceFile}
            </h2>
            <div className="flex items-center gap-3 mt-1">
              <span className="cs-meta">
                Page {currentPage} of {totalPages}
              </span>
              {transactionContext && (
                <div className="flex items-center gap-2 cs-meta px-2 py-1 rounded" style={{ background: 'var(--color-needs-human-tint)' }}>
                  <span>{transactionContext.date}</span>
                  <span>•</span>
                  <span className="truncate max-w-[150px]">{transactionContext.vendor}</span>
                  <span>•</span>
                  <span style={{ fontWeight: 600, color: 'var(--color-verified)' }}>
                    <FinancialValue value={transactionContext.amount} />
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Search Toggle */}
            <button
              onClick={() => setShowSearchPanel(!showSearchPanel)}
              className="p-2 rounded-lg"
              style={showSearchPanel
                ? { background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }
                : { color: 'var(--color-text-muted)' }}
              title="Search in PDF (Ctrl+F)"
            >
              <Search size={18} />
            </button>

            {/* Page Navigation */}
            <div className="flex items-center rounded-lg" style={{ background: 'var(--color-bg-elevated)' }}>
              <button
                onClick={handlePreviousPage}
                disabled={currentPage <= 1}
                className="p-2 rounded-l-lg disabled:opacity-30 disabled:cursor-not-allowed"
                style={{ color: 'var(--color-text-secondary)' }}
                title="Previous Page (←)"
              >
                <ChevronLeft size={18} />
              </button>
              <input
                type="number"
                value={currentPage}
                onChange={handlePageInput}
                min={1}
                max={totalPages}
                className="w-12 text-center cs-body bg-transparent border-0 focus:ring-0"
                style={{ fontWeight: 500 }}
              />
              <button
                onClick={handleNextPage}
                disabled={currentPage >= totalPages}
                className="p-2 rounded-r-lg disabled:opacity-30 disabled:cursor-not-allowed"
                style={{ color: 'var(--color-text-secondary)' }}
                title="Next Page (→)"
              >
                <ChevronRight size={18} />
              </button>
            </div>

            <div className="w-px h-6" style={{ background: 'var(--color-border)' }}></div>

            {/* Zoom Controls */}
            <div className="flex items-center rounded-lg" style={{ background: 'var(--color-bg-elevated)' }}>
              <button
                onClick={handleZoomOut}
                disabled={zoom <= 50}
                className="p-2 rounded-l-lg disabled:opacity-30"
                style={{ color: 'var(--color-text-secondary)' }}
                title="Zoom Out"
              >
                <ZoomOut size={18} />
              </button>
              <span className="cs-body w-14 text-center" style={{ fontWeight: 500 }}>{zoom}%</span>
              <button
                onClick={handleZoomIn}
                disabled={zoom >= 200}
                className="p-2 rounded-r-lg disabled:opacity-30"
                style={{ color: 'var(--color-text-secondary)' }}
                title="Zoom In"
              >
                <ZoomIn size={18} />
              </button>
            </div>

            <div className="w-px h-6" style={{ background: 'var(--color-border)' }}></div>

            {/* Action Buttons */}
            <button
              onClick={handleOpenInNewTab}
              disabled={!pdfUrl}
              className="p-2 rounded-lg disabled:opacity-30"
              style={{ color: 'var(--color-text-muted)' }}
              title="Open in New Tab"
            >
              <ExternalLink size={18} />
            </button>
            <button
              onClick={handleDownload}
              disabled={!pdfUrl}
              className="flex items-center gap-1 px-3 py-2 cs-body rounded-lg disabled:opacity-30"
              style={{ fontWeight: 500, color: 'var(--color-on-brand)', background: 'var(--color-brand)' }}
            >
              <Download size={16} />
              Download
            </button>

            {/* Close Button */}
            <button
              onClick={onClose}
              className="p-2 rounded-lg ml-2"
              style={{ color: 'var(--color-text-muted)' }}
              title="Close (Esc)"
              data-testid="pdf-viewer-close"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Search Panel */}
        {showSearchPanel && (
          <div className="flex items-center gap-3 px-4 py-3" style={{ background: 'var(--color-bg-elevated)', borderBottom: '1px solid var(--color-border-sub)' }}>
            <Search size={18} style={{ color: 'var(--color-text-dim)' }} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Search in document..."
              className="flex-1 px-3 py-2 rounded-lg cs-body focus:outline-none"
              style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}
              autoFocus
            />
            <button
              onClick={() => handleSearch()}
              disabled={isSearching || !searchQuery.trim()}
              className="px-4 py-2 rounded-lg cs-body disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              style={{ color: 'var(--color-on-brand)', background: 'var(--color-brand)' }}
            >
              {isSearching ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
              Search
            </button>

            {searchResults.length > 0 && (
              <div className="flex items-center gap-2 ml-2">
                <span className="cs-meta">
                  {currentResultIndex + 1} of {searchResults.length}
                </span>
                <button
                  onClick={goToPreviousResult}
                  className="p-1 rounded"
                  style={{ color: 'var(--color-text-secondary)' }}
                  title="Previous result"
                >
                  <ArrowUp size={16} />
                </button>
                <button
                  onClick={goToNextResult}
                  className="p-1 rounded"
                  style={{ color: 'var(--color-text-secondary)' }}
                  title="Next result"
                >
                  <ArrowDown size={16} />
                </button>
              </div>
            )}

            {searchQuery && searchResults.length === 0 && !isSearching && (
              <span className="cs-meta" style={{ color: 'var(--color-needs-human-text)' }}>No results found</span>
            )}

            <button
              onClick={() => {
                setShowSearchPanel(false);
                setSearchQuery('');
                setSearchResults([]);
              }}
              className="p-1"
              style={{ color: 'var(--color-text-muted)' }}
            >
              <X size={18} />
            </button>
          </div>
        )}

        {/* Search Results Panel (for current page) */}
        {showSearchPanel && currentPageResults.length > 0 && (
          <div className="px-4 py-2 max-h-32 overflow-y-auto" style={{ background: 'var(--color-needs-human-tint)', borderBottom: '1px solid var(--color-needs-human-tint-bd)' }}>
            <div className="cs-label mb-1" style={{ color: 'var(--color-needs-human-text)' }}>
              {currentPageResults.length} result(s) on this page:
            </div>
            <div className="space-y-1">
              {currentPageResults.slice(0, 5).map((result, idx) => (
                <div key={idx} className="cs-body rounded px-2 py-1" style={{ background: 'var(--color-bg-surface)' }}>
                  ...{result.text.substring(Math.max(0, result.matchStart - 20), result.matchEnd + 30)}...
                </div>
              ))}
              {currentPageResults.length > 5 && (
                <div className="cs-meta">
                  +{currentPageResults.length - 5} more results
                </div>
              )}
            </div>
          </div>
        )}

        {/* PDF Viewer Area */}
        <div className="flex-1 overflow-auto p-4" style={{ background: 'var(--color-bg-base)' }}>
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <Loader2 className="animate-spin h-12 w-12 mx-auto mb-4" style={{ color: 'var(--color-brand)' }} />
                <p className="cs-body">Loading document...</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-full">
              <div className="rounded-lg shadow p-8 max-w-md text-center" style={{ background: 'var(--color-bg-surface)' }}>
                <FileText size={48} className="mx-auto mb-4" style={{ color: 'var(--color-needs-human-text)' }} />
                <h4 className="cs-h3 mb-2">Unable to Load Document</h4>
                <p className="cs-body mb-4" style={{ color: 'var(--color-text-muted)' }}>{error}</p>
                <div className="flex gap-3 justify-center">
                  <button
                    onClick={fetchPdfUrl}
                    className="px-4 py-2 rounded-lg cs-body"
                    style={{ color: 'var(--color-on-brand)', background: 'var(--color-brand)' }}
                  >
                    Try Again
                  </button>
                  <button
                    onClick={onClose}
                    className="px-4 py-2 rounded-lg cs-body"
                    style={{ background: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          ) : pdfUrl ? (
            <div className="flex flex-col items-center h-full w-full">
              {/* PDF container with highlight overlay */}
              {/*
                Coordinate mapping: Textract bbox is normalized 0-1 relative to page.
                With FitH, the PDF page width fills the iframe width (e.g. 900px).
                The rendered page height = width * (11/8.5) for US Letter ≈ 1165px.
                We use pixel positioning for top/height since the rendered page is
                taller than the container, and percentage would be relative to the
                shorter container height.
              */}
              <div className="relative w-full h-full" style={{ maxWidth: '900px', overflow: 'hidden' }}>
                {/* Direct PDF embed with page support — toolbar=0 removes Chrome's built-in toolbar */}
                <iframe
                  ref={iframeRef}
                  src={`${pdfUrl}#page=${currentPage}&view=FitH&toolbar=0`}
                  className="shadow-lg rounded w-full h-full border-0"
                  style={{ minHeight: '600px', background: 'var(--color-bg-surface)' }}
                  title={`PDF Viewer - ${sourceFile}`}
                  key={`pdf-${currentPage}`}
                />

                {/* Highlight overlays for search results on current page */}
                {searchResults
                  .filter(r => r.pageNumber === currentPage && r.bbox)
                  .map((result, idx) => {
                    const isActive = searchResults.indexOf(result) === currentResultIndex;
                    const bbox = result.bbox!;
                    // US Letter: page height = width * (11/8.5)
                    // Container width is 900px (maxWidth), so rendered page height ≈ 1165px
                    // bbox coords are normalized (0-1) relative to the full page
                    // FitH: page width = container width, so left/width use % directly
                    // top/height must use pixels based on rendered page height
                    const CONTAINER_WIDTH = 900;
                    const PAGE_HEIGHT = CONTAINER_WIDTH * (11 / 8.5);
                    // Chrome PDF viewer adds ~4px padding at top
                    const TOP_OFFSET = 4;
                    return (
                      <div
                        key={`highlight-${idx}`}
                        className={isActive ? 'pdf-highlight-pulse' : ''}
                        style={{
                          position: 'absolute',
                          left: `${bbox.Left * 100}%`,
                          top: `${bbox.Top * PAGE_HEIGHT + TOP_OFFSET}px`,
                          width: `${bbox.Width * 100}%`,
                          height: `${Math.max(bbox.Height * PAGE_HEIGHT, 18)}px`,
                          backgroundColor: isActive
                            ? 'rgba(255, 102, 0, 0.45)'
                            : 'rgba(255, 102, 0, 0.2)',
                          border: isActive
                            ? '2px solid rgba(255, 102, 0, 0.9)'
                            : '1px solid rgba(255, 102, 0, 0.5)',
                          borderRadius: '3px',
                          pointerEvents: 'none',
                          zIndex: 10,
                          boxShadow: isActive
                            ? '0 0 8px rgba(255, 102, 0, 0.6)'
                            : 'none',
                        }}
                      />
                    );
                  })}
              </div>

              {/* Quick action bar below PDF */}
              <div className="mt-3 flex items-center gap-4 cs-meta">
                <span style={{ color: 'var(--color-text-muted)' }}>Can't see the PDF?</span>
                <a
                  href={`${pdfUrl}#page=${currentPage}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 hover:underline"
                  style={{ color: 'var(--color-needs-human-text)', fontWeight: 500 }}
                >
                  <ExternalLink size={14} />
                  Open in new tab
                </a>
              </div>
            </div>
          ) : null}
        </div>

        {/* Footer */}
        <div className="px-4 py-2 rounded-b-lg" style={{ borderTop: '1px solid var(--color-border)', background: 'var(--color-bg-elevated)' }}>
          <div className="flex items-center justify-between cs-meta">
            <div className="flex items-center gap-3">
              <span>
                Viewing page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
              </span>
              {sourcePage && sourcePage > totalPages && (
                <span className="cs-chip px-2 py-1 rounded inline-flex items-center gap-1" style={{ color: 'var(--color-needs-human-text)', background: 'var(--color-needs-human-tint)' }}>
                  <AlertTriangle size={12} /> Transaction references page {sourcePage}, but document only has {totalPages} pages
                </span>
              )}
              {sourcePage && currentPage !== sourcePage && sourcePage <= totalPages && (
                <button
                  onClick={jumpToTransactionPage}
                  className="hover:underline"
                  style={{ color: 'var(--color-needs-human-text)' }}
                >
                  ↩ Jump to transaction (page {sourcePage})
                </button>
              )}
            </div>
            <span className="hidden sm:inline" style={{ color: 'var(--color-text-dim)' }}>
              <kbd className="px-1 py-0.5 rounded cs-chip mx-1" style={{ background: 'var(--color-bg-surface)' }}>Ctrl+F</kbd> to search,
              <kbd className="px-1 py-0.5 rounded cs-chip mx-1" style={{ background: 'var(--color-bg-surface)' }}>←</kbd> /
              <kbd className="px-1 py-0.5 rounded cs-chip mx-1" style={{ background: 'var(--color-bg-surface)' }}>→</kbd> to navigate,
              <kbd className="px-1 py-0.5 rounded cs-chip mx-1" style={{ background: 'var(--color-bg-surface)' }}>Esc</kbd> to close
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
